/**
 * Signup API client for the public /signup page, built to
 * `docs/build/contract.md` (Travis D3: passwordless, two steps):
 *
 *   POST /api/app/auth/signup         body              → { ok: true, next: "verify" }  (emails a 6-digit code)
 *   POST /api/app/auth/verify-signup  same body + code  → { ok: true, redirectTo }
 *   errors                                              → { ok: false, field?, message, code? }
 *
 * `smsConsent: true` is required whenever `phone` is sent.
 *
 * Set `VITE_SIGNUP_MOCK=true` in development to click through the page before
 * the backend endpoint ships (any code except 000001 verifies). The mock never
 * runs in a production build.
 */
import type { Attribution } from "./attribution";

export type SignupRole = "host" | "property_manager" | "researcher";

export interface SignupPayload {
  name: string;
  email: string;
  propertyCount: number;
  role: SignupRole;
  phone?: string;
  smsConsent?: boolean;
  acceptedTerms: true;
  offer?: "syre-lifetime";
  /** SYRE stacking: 1–4 units of 5 lifetime properties, passed through to checkout. */
  units?: number;
  eventId?: number;
  attribution?: Attribution;
}

export type SignupResult =
  | { ok: true; next: "verify" }
  | { ok: false; field?: string; message: string };

export type VerifyResult =
  | { ok: true; redirectTo: string }
  | { ok: false; field?: string; message: string };

export const SIGNUP_ENDPOINT = "/api/app/auth/signup";
export const VERIFY_SIGNUP_ENDPOINT = "/api/app/auth/verify-signup";

const GENERIC_ERROR = "Something went wrong. Please try again.";

function mockEnabled(): boolean {
  return Boolean(import.meta.env.DEV) && import.meta.env.VITE_SIGNUP_MOCK === "true";
}

async function postJson(url: string, body: unknown): Promise<{ status: number; data: Record<string, unknown> | null }> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  let data: Record<string, unknown> | null = null;
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    data = null;
  }
  return { status: res.status, data };
}

function errorFrom(data: Record<string, unknown> | null): { ok: false; field?: string; message: string } {
  const raw = String(data?.message ?? "").trim();
  const message = typeof data?.message === "string" && raw ? raw : GENERIC_ERROR;
  const field = typeof data?.field === "string" ? data.field : undefined;
  return field ? { ok: false, field, message } : { ok: false, message };
}

export async function submitSignup(payload: SignupPayload): Promise<SignupResult> {
  if (mockEnabled()) return { ok: true, next: "verify" };
  try {
    const { status, data } = await postJson(SIGNUP_ENDPOINT, payload);
    if (status >= 200 && status < 300 && data?.ok === true) return { ok: true, next: "verify" };
    return errorFrom(data);
  } catch {
    return { ok: false, message: GENERIC_ERROR };
  }
}

export async function verifySignupCode(payload: SignupPayload, code: string): Promise<VerifyResult> {
  if (mockEnabled()) {
    return code === "000001"
      ? { ok: false, field: "code", message: "That code is incorrect." }
      : { ok: true, redirectTo: "/app/dashboard" };
  }
  try {
    const { status, data } = await postJson(VERIFY_SIGNUP_ENDPOINT, { ...payload, code });
    if (status >= 200 && status < 300 && data?.ok === true && typeof data.redirectTo === "string") {
      return { ok: true, redirectTo: data.redirectTo };
    }
    return errorFrom(data);
  } catch {
    return { ok: false, message: GENERIC_ERROR };
  }
}

/** "(404) 555-1234" / "4045551234" / "+1 404…" → "+14045551234"; null when it can't be a phone number. */
export function toE164(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}
